// Server refusals (security sprint): tells a refused write apart from a network or other error,
// so a page can say what happened instead of "Check your connection". Pages show only the fixed
// text below, never the server's message.
//
// - Members only (PR 9): createPublicJournal, createChatRoom, createBook and createComment for a
//   user without a paid membership. gnjAmplifyIsMember throws "Unauthorized: <op> is for
//   members" (errorType Lambda:Unhandled); MembersOnly itself fails closed with errorType
//   "MembersOnly".
// - Other refusals: owner rules ("Not Authorized to access <op> on type Mutation",
//   "Unauthorized on [field]"), PublicJournalBookOwner ("Unauthorized on [publicJournalBookId]"),
//   a client-chosen book ID, and gnjAmplifyIsMember's own "Unauthorized: ..." refusals.

export const MEMBERS_ONLY = 'membersOnly'
export const REFUSED = 'refused'

export const REFUSED_MESSAGE = "You don't have permission to do that."

const errorsOf = (err) => (Array.isArray(err?.errors) ? err.errors : [])

const isMembersOnly = (e) =>
  e?.errorType === 'MembersOnly' ||
  /^Unauthorized: (.+ is for members|membership check failed)$/.test(e?.message ?? '')

// A refusal of the operation itself. One on a nested field (path longer than the operation) is a
// related row the caller can't read: the write still happened. Only the server's own shapes count:
// a bare "Unauthorized" (no errorType) is how Amplify reports an HTTP 401, an expired or revoked
// session, which is not a refusal of this write.
const isRefused = (e) =>
  (!Array.isArray(e?.path) || e.path.length <= 1) &&
  (e?.errorType === 'Unauthorized' || /^(Unauthorized(: | on \[)|Not Authorized to access )/.test(e?.message ?? ''))

// MEMBERS_ONLY, REFUSED, or null for anything else (network, throttling, a server fault).
export function refusalOf(err) {
  const errors = errorsOf(err)
  if (errors.some(isMembersOnly)) return MEMBERS_ONLY
  if (errors.some(isRefused)) return REFUSED
  return null
}

// The field named by an "Unauthorized on [field]" refusal (a schema field name), or null.
export function refusedField(err) {
  for (const e of errorsOf(err)) {
    const m = /^Unauthorized on \[(\w+)\]$/.exec(e?.message ?? '')
    if (m) return m[1]
  }
  return null
}

// The text for a refused write: `refused` for a refusal (members-only included, for pages that
// have no upgrade popup), otherwise `fallback`.
export function refusalMessage(err, { refused = REFUSED_MESSAGE, fallback }) {
  return refusalOf(err) ? refused : fallback
}

// Publishing or updating a journal (WriteJournal, EditJournal) failed with something other than a
// members-only refusal (those show the upgrade popup).
export function publishFailedMessage(err) {
  if (!refusalOf(err)) return "Your journal couldn't be published. Check your connection and try again."
  const field = refusedField(err)
  if (field === 'publicJournalBookId' || field === 'journalBookId') {
    return "Your journal couldn't be published: you can only add it to your own book."
  }
  return "Your journal couldn't be published: you don't have permission to do that."
}
